import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import style from './VisitaForm.module.css';

const VisitaForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector(state => state.user);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const agenteName = user?.user
    ? `${user.user.name || ''} ${user.user.last_name || ''}`.trim()
    : '';

  const [formData, setFormData] = useState({
    visitante: '',
    agente: agenteName,
    fecha: new Date().toISOString().split('T')[0],
    descripcion: '',
    propertyId: '',
    gusto: { yes: false, no: false },
    calificacionUbicacion: { excelente: false, buena: false, regular: false, mala: false },
    espaciosYComodidades: { muySatisfactorio: false, satisfactorio: false, insatisfactorio: false },
    calidadPrecio: { excelente: false, buena: false, regular: false, mala: false },
    estado: { excelente: false, buena: false, regular: false, mala: false },
    general: { excelente: false, muyBuena: false, buena: false, regular: false, mala: false },
    comprar: { yes: false, no: false },
    verOtras: { yes: false, no: false, maybe: false },
  });

  useEffect(() => {
    if (id) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        propertyId: id,
      }));
    }
  }, [id]);

  useEffect(() => {
    if (agenteName && !formData.agente) {
      setFormData(prev => ({ ...prev, agente: agenteName }));
    }
  }, [agenteName]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
  
    if (type === 'date') {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...Object.fromEntries(Object.keys(prev[section]).map((key) => [key, false])),
          [field]: checked,
        },
      }));
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleOptionSelect = (section, field) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...Object.fromEntries(Object.keys(prev[section]).map((key) => [key, false])),
        [field]: true,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
  
    try {
      const response = await axios.post('/visita', {
        ...formData,
        fecha: formData.fecha,
      });
  
      setSuccess(true);
      setTimeout(() => navigate('/home'), 1200);
    } catch (error) {
      console.error('Error al crear la visita:', error);
      setSubmitting(false);
    }
  };

  // Helper to get emoji for rating
  const ratingEmoji = {
    excelente: '🌟',
    muyBuena: '⭐',
    buena: '👍',
    regular: '😐',
    mala: '👎',
    muySatisfactorio: '🏆',
    satisfactorio: '✅',
    insatisfactorio: '❌',
  };

  const OptionCard = ({ section, field, label, emoji }) => {
    const isSelected = formData[section][field];
    return (
      <div
        className={`${style.optionCard} ${isSelected ? style.optionCardSelected : ''}`}
        onClick={() => handleOptionSelect(section, field)}
      >
        {emoji && <span className={style.optionEmoji}>{emoji}</span>}
        <span className={style.optionLabel}>{label}</span>
        <div className={`${style.optionRadio} ${isSelected ? style.optionRadioSelected : ''}`}>
          {isSelected && <div className={style.optionRadioDot} />}
        </div>
      </div>
    );
  };
  
  if (success) {
    return (
      <div className={style.successOverlay}>
        <div className={style.successCard}>
          <div className={style.successIcon}>✓</div>
          <h2>¡Visita registrada!</h2>
          <p>Redirigiendo al inicio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={style.visitaFormContainer}>
      <div className={style.headerBar}>
        <div className={style.headerContent}>
          <h2 className={style.title}>Anotar Visita</h2>
          <p className={style.propertyBadge}>Propiedad #{id}</p>
        </div>
        <button type="button" className={style.backButton} onClick={() => navigate(-1)}>
          ← Volver
        </button>
      </div>

      <form onSubmit={handleSubmit} className={style.form}>
        {/* Sección 1: Información básica */}
        <div className={style.section}>
          <div className={style.sectionHeader}>
            <span className={style.sectionIcon}>📋</span>
            <h3 className={style.sectionTitle}>Información de la visita</h3>
          </div>

          <div className={style.inputRow}>
            <div className={style.formGroup}>
              <label htmlFor="visitante" className={style.label}>Visitante</label>
              <input
                type="text"
                id="visitante"
                name="visitante"
                className={style.input}
                value={formData.visitante}
                onChange={handleInputChange}
                placeholder="Nombre del visitante"
                required
              />
            </div>

            <div className={style.formGroup}>
              <label htmlFor="agente" className={style.label}>Agente</label>
              <input
                type="text"
                id="agente"
                name="agente"
                className={style.input}
                value={formData.agente}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className={style.inputRow}>
            <div className={style.formGroup}>
              <label htmlFor="fecha" className={style.label}>Fecha de visita</label>
              <input
                type="date"
                id="fecha"
                name="fecha"
                className={style.input}
                value={formData.fecha}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className={style.formGroup} />
          </div>

          <div className={style.formGroup}>
            <label htmlFor="descripcion" className={style.label}>Descripción / Notas</label>
            <textarea
              id="descripcion"
              name="descripcion"
              className={style.textarea}
              value={formData.descripcion}
              onChange={handleInputChange}
              placeholder="Escribí notas sobre la visita..."
              rows={3}
            ></textarea>
          </div>
        </div>

        {/* Sección 2: Feedback del visitante */}
        <div className={style.section}>
          <div className={style.sectionHeader}>
            <span className={style.sectionIcon}>💬</span>
            <h3 className={style.sectionTitle}>Feedback del visitante</h3>
          </div>

          {/* ¿Te gustó? */}
          <div className={style.questionBlock}>
            <p className={style.questionTitle}>¿Le gustó la propiedad?</p>
            <div className={style.optionsRow}>
              <OptionCard section="gusto" field="yes" label="Sí" emoji="😊" />
              <OptionCard section="gusto" field="no" label="No" emoji="😞" />
            </div>
          </div>

          {/* Ubicación */}
          <div className={style.questionBlock}>
            <p className={style.questionTitle}>¿Qué le pareció la ubicación?</p>
            <div className={style.optionsRow}>
              {['excelente', 'buena', 'regular', 'mala'].map((option) => (
                <OptionCard
                  key={option}
                  section="calificacionUbicacion"
                  field={option}
                  label={option.charAt(0).toUpperCase() + option.slice(1)}
                  emoji={ratingEmoji[option]}
                />
              ))}
            </div>
          </div>

          {/* Estado general */}
          <div className={style.questionBlock}>
            <p className={style.questionTitle}>¿Cómo calificaría el estado general?</p>
            <div className={style.optionsRow}>
              {['excelente', 'buena', 'regular', 'mala'].map((option) => (
                <OptionCard
                  key={option}
                  section="estado"
                  field={option}
                  label={option.charAt(0).toUpperCase() + option.slice(1)}
                  emoji={ratingEmoji[option]}
                />
              ))}
            </div>
          </div>

          {/* Espacios y comodidades */}
          <div className={style.questionBlock}>
            <p className={style.questionTitle}>¿Qué le parecieron los espacios y comodidades?</p>
            <div className={style.optionsRow}>
              {[
                { key: 'muySatisfactorio', label: 'Muy satisfactorio' },
                { key: 'satisfactorio', label: 'Satisfactorio' },
                { key: 'insatisfactorio', label: 'Insatisfactorio' },
              ].map(({ key, label }) => (
                <OptionCard
                  key={key}
                  section="espaciosYComodidades"
                  field={key}
                  label={label}
                  emoji={ratingEmoji[key]}
                />
              ))}
            </div>
          </div>

          {/* Calidad/Precio */}
          <div className={style.questionBlock}>
            <p className={style.questionTitle}>¿Cómo considera la relación calidad/precio?</p>
            <div className={style.optionsRow}>
              {['excelente', 'buena', 'regular', 'mala'].map((option) => (
                <OptionCard
                  key={option}
                  section="calidadPrecio"
                  field={option}
                  label={option.charAt(0).toUpperCase() + option.slice(1)}
                  emoji={ratingEmoji[option]}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Sección 3: Calificación general */}
        <div className={style.section}>
          <div className={style.sectionHeader}>
            <span className={style.sectionIcon}>⭐</span>
            <h3 className={style.sectionTitle}>Calificación general</h3>
          </div>

          <div className={style.questionBlock}>
            <p className={style.questionTitle}>Calificación general de la propiedad</p>
            <div className={style.optionsRow}>
              {[
                { key: 'excelente', label: 'Excelente' },
                { key: 'muyBuena', label: 'Muy buena' },
                { key: 'buena', label: 'Buena' },
                { key: 'regular', label: 'Regular' },
                { key: 'mala', label: 'Mala' },
              ].map(({ key, label }) => (
                <OptionCard
                  key={key}
                  section="general"
                  field={key}
                  label={label}
                  emoji={ratingEmoji[key]}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Sección 4: Intención */}
        <div className={style.section}>
          <div className={style.sectionHeader}>
            <span className={style.sectionIcon}>🏠</span>
            <h3 className={style.sectionTitle}>Intención del visitante</h3>
          </div>

          <div className={style.questionBlock}>
            <p className={style.questionTitle}>¿Compraría/Alquilaría esta propiedad?</p>
            <div className={style.optionsRow}>
              <OptionCard section="comprar" field="yes" label="Sí" emoji="✅" />
              <OptionCard section="comprar" field="no" label="No" emoji="❌" />
            </div>
          </div>

          <div className={style.questionBlock}>
            <p className={style.questionTitle}>¿Desea ver otras propiedades similares?</p>
            <div className={style.optionsRow}>
              <OptionCard section="verOtras" field="yes" label="Sí" emoji="👀" />
              <OptionCard section="verOtras" field="no" label="No" emoji="✋" />
              <OptionCard section="verOtras" field="maybe" label="Tal vez" emoji="🤔" />
            </div>
          </div>
        </div>

        <div className={style.submitArea}>
          <button
            type="button"
            className={style.cancelButton}
            onClick={() => navigate(-1)}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={style.submitButton}
            disabled={submitting}
          >
            {submitting ? (
              <span className={style.spinnerWrap}>
                <span className={style.spinner} />
                Registrando...
              </span>
            ) : (
              'Registrar Visita'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VisitaForm;

